import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "./api";

const AuthContext = createContext(null);
const TOKEN_KEY = "aura_token";
const DEMO_KEY = "hexa_demo";

// Fake demo user — read-only preview identity. All backend writes are intercepted.
const DEMO_USER = {
  id: "demo-user",
  username: "demo",
  address: "demo@hexawebos.com",
  name: "Demo Explorer",
  role: "boss",  // demo user sees ALL features (including boss-only) for exploration
  account_type: "business",
  company_name: "Acme Demo Ltd.",
  workspace: "demo@hexawebos.com",
  plan: "business",
  ai_credits_used: 12,
  ai_credits_limit: 1000,
  designation: "CEO",
  demo: true,
  is_super_admin: false,
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [demo, setDemo] = useState(() => localStorage.getItem(DEMO_KEY) === "1");
  const [appAccess, setAppAccess] = useState({ hidden: [], locked: [], override: "full" });

  const setToken = (tok) => {
    if (tok) localStorage.setItem(TOKEN_KEY, tok);
    else localStorage.removeItem(TOKEN_KEY);
    api.defaults.headers.common["Authorization"] = tok ? `Bearer ${tok}` : "";
  };

  const fetchMe = useCallback(async () => {
    const tok = localStorage.getItem(TOKEN_KEY);
    if (!tok) { setChecking(false); return; }
    api.defaults.headers.common["Authorization"] = `Bearer ${tok}`;
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Real auth wins over demo
      if (demo) { setDemo(false); localStorage.removeItem(DEMO_KEY); }
    } catch {
      setToken(null);
      setUser(null);
    }
    setChecking(false);
  }, [demo]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const fetchAppAccess = useCallback(async () => {
    try {
      const { data } = await api.get("/me/app-access");
      setAppAccess(data);
    } catch {
      setAppAccess({ hidden: [], locked: [], override: "full" });
    }
  }, []);

  useEffect(() => {
    if (user && !user.demo) fetchAppAccess();
    else setAppAccess({ hidden: [], locked: [], override: "full" });
  }, [user, fetchAppAccess]);

  // Listen for session-expiry from the api layer and boot the user back to
  // the login screen with a friendly flash — no scary "Token expired" toast.
  useEffect(() => {
    const onExpired = () => {
      try { localStorage.removeItem(TOKEN_KEY); } catch (_) { /* noop */ }
      api.defaults.headers.common["Authorization"] = "";
      setUser(null);
      setChecking(false);
      // A very lightweight toast — no dependency on a toast lib.
      if (typeof window !== "undefined" && !window.__hexaExpiryFlashed) {
        window.__hexaExpiryFlashed = true;
        const flash = document.createElement("div");
        flash.textContent = "Signed out — please sign in again";
        Object.assign(flash.style, {
          position: "fixed", top: "18px", left: "50%", transform: "translateX(-50%)",
          background: "linear-gradient(120deg,#8b5cf6,#38bdf8)", color: "#fff",
          fontFamily: "Inter, system-ui, sans-serif", fontSize: "13px", fontWeight: "600",
          padding: "10px 18px", borderRadius: "999px", zIndex: "999999",
          boxShadow: "0 10px 24px rgba(15,23,42,0.25)",
        });
        document.body.appendChild(flash);
        setTimeout(() => { flash.remove(); window.__hexaExpiryFlashed = false; }, 3500);
      }
    };
    window.addEventListener("hexa:session-expired", onExpired);
    return () => window.removeEventListener("hexa:session-expired", onExpired);
  }, []);

  const login = async (address, password) => {
    const { data } = await api.post("/auth/login", { address, password });
    setToken(data.token);
    setUser(data.user);
    exitDemo();
    return data.user;
  };

  const signup = async (usernameOrEmail, name, password, accountType = "individual", companyName = "", ref = "", plan = "", contactEmail = "", acceptedTerms = false, phone = "") => {
    const isExternal = (usernameOrEmail || "").includes("@");
    const payload = {
      name, password,
      account_type: accountType,
      company_name: companyName,
      contact_email: contactEmail,
      accepted_terms: acceptedTerms,
      phone,
    };
    if (plan) payload.plan = plan;
    if (isExternal) payload.email = usernameOrEmail;
    else payload.username = usernameOrEmail;
    if (ref) payload.ref = ref;
    const { data } = await api.post("/auth/signup", payload);
    setToken(data.token);
    setUser(data.user);
    exitDemo();
    return data.user;
  };

  const logout = () => {
    // Forget the open windows so the next person to sign in on this tab
    // doesn't inherit them (see WM_SESSION_KEY in store.jsx).
    try { sessionStorage.removeItem("hexa_open_windows"); } catch (_) { /* noop */ }
    setToken(null);
    setUser(null);
    exitDemo();
  };

  const enterDemo = () => {
    localStorage.setItem(DEMO_KEY, "1");
    setDemo(true);
    setUser(DEMO_USER);
  };
  const exitDemo = () => {
    localStorage.removeItem(DEMO_KEY);
    setDemo(false);
    // Only clear the fake user; real users are unaffected.
    setUser((u) => (u && u.demo ? null : u));
  };

  // Sync fake user when entering demo (in case component re-mounts)
  useEffect(() => {
    if (demo && !user) setUser(DEMO_USER);
    // eslint-disable-next-line
  }, [demo]);

  return (
    <AuthContext.Provider value={{
      user, setUser, checking, login, signup, logout,
      refresh: fetchMe,
      demo, enterDemo, exitDemo,
      appAccess, refreshAppAccess: fetchAppAccess,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("AuthContext missing");
  return ctx;
};
